import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { firmInfo } from '../data/mock';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'Our Team' },
    { path: '/practice-areas', label: 'Practice Areas' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/thought-leadership', label: 'Exclusive Insights' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-gold/5' : 'bg-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-gold/20 blur-xl group-hover:bg-gold/30 transition-all duration-300"></div>
              <img
                src={firmInfo.logo}
                alt={firmInfo.name}
                className="h-12 w-auto relative z-10 transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 group ${isActive(link.path)
                  ? 'text-gold'
                  : 'text-gray-300 hover:text-gold'
                  }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-gold via-gold-light to-gold transform origin-left transition-transform duration-300 ${isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                ></span>
              </Link>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <Link
            to="/contact"
            className="hidden lg:block px-6 py-2.5 bg-gradient-to-r from-gold to-gold-light text-black font-semibold text-sm rounded-md hover:shadow-lg hover:shadow-gold/30 transform hover:scale-105 transition-all duration-300"
          >
            Schedule Consultation
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gold hover:text-gold-light transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen
          ? 'max-h-screen opacity-100'
          : 'max-h-0 opacity-0 overflow-hidden'
          }`}
      >
        <nav className="bg-black/98 backdrop-blur-lg border-t border-gold/20 px-4 py-6 space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-base font-medium rounded-lg transition-all duration-300 ${isActive(link.path)
                ? 'bg-gold/10 text-gold border-l-4 border-gold'
                : 'text-gray-300 hover:bg-gold/5 hover:text-gold hover:border-l-4 hover:border-gold/50'
                }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block mt-4 px-6 py-3 bg-gradient-to-r from-gold to-gold-light text-black font-semibold text-center rounded-lg hover:shadow-lg hover:shadow-gold/30 transition-all duration-300"
          >
            Schedule Consultation
          </Link>
        </nav>
      </div>
    </header>
  );
};
